import { createContext, useContext, useEffect, useState } from "react";

import { request } from "@/lib/api";

const DEFAULT_BRANDING = { client_id: null, name: "Ivory Health Screening", notification_from_name: "Ivory Health", logo_url: null, brand_color: null };

const THEME_VARS = ["--moss", "--moss-dark", "--soft-green"];

function darken(hex, factor) {
  const [r, g, b] = [hex.slice(1, 3), hex.slice(3, 5), hex.slice(5, 7)].map((part) => parseInt(part, 16));
  const clamp = (value) => Math.max(0, Math.min(255, Math.round(value)));
  return `#${[r, g, b].map((channel) => clamp(channel * factor).toString(16).padStart(2, "0")).join("")}`;
}

function lightenTint(hex, factor) {
  const [r, g, b] = [hex.slice(1, 3), hex.slice(3, 5), hex.slice(5, 7)].map((part) => parseInt(part, 16));
  const clamp = (value) => Math.max(0, Math.min(255, Math.round(value)));
  return `#${[r, g, b].map((channel) => clamp(channel + (255 - channel) * factor).toString(16).padStart(2, "0")).join("")}`;
}

// Orgs configured through the admin dashboard supply one brand_color; the
// rest of the palette (a darker shade for hover states, a light tint for
// selected/soft backgrounds) is derived from it so every org gets a full,
// consistent theme without needing to specify each variable separately.
function applyBrandColor(hex) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
    THEME_VARS.forEach((name) => document.documentElement.style.removeProperty(name));
    return;
  }
  document.documentElement.style.setProperty("--moss", hex);
  document.documentElement.style.setProperty("--moss-dark", darken(hex, 0.78));
  document.documentElement.style.setProperty("--soft-green", lightenTint(hex, 0.94));
}

const TenantContext = createContext(null);

export function TenantProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);

  useEffect(() => {
    request("/tenant/branding", { headers: { "X-Forwarded-Host": window.location.hostname } })
      .then((result) => {
        setBranding(result);
        document.title = result.name;
      })
      .catch(() => setBranding(DEFAULT_BRANDING));
  }, []);

  useEffect(() => {
    // An inline style here outranks a stylesheet rule like ROHM's
    // [data-tenant="rohm"] block in App.css — but ROHM's client row has no
    // brand_color set, so this stays inert for ROHM and that CSS block
    // remains the effective source of truth. Setting brand_color on ROHM's
    // row later would intentionally supersede it — one config path going
    // forward, not two competing ones.
    applyBrandColor(branding.brand_color);
    return () => applyBrandColor(null);
  }, [branding.brand_color]);

  return <TenantContext.Provider value={branding}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within TenantProvider.");
  }
  return context;
}
